import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo
} from 'react';
import Card from '@components/Card';
import PlayButton from './play.svg';
import styles from './Media.module.scss';
import useEvent from '@hooks/useEvent';
// import useMediaModal from '@hooks/useMediaModal';
// import Player from 'react-player';
import classNames from 'classnames';
import { isBrowser, createPortal } from '@helpers';
import { isNumber } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { RemoveScroll } from 'react-remove-scroll';
import FocusLock from 'react-focus-lock';

export default function Media({
  src,
  style = {},
  className,
  playerClassName,
  playerWrapperClassName,
  title,
  playerStyle,
  maxWidth,
  isInline,
  children,
  thumbnail,
  thumbnailHeight,
  duration: _duration = null,
  playerProps
}) {
  const [isOpen, setIsOpen] = useState(!!isInline);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(_duration);
  const [isLoadingDuration, setIsLoadingDuration] = useState(
    !isInline && duration === null
  );
  const previewRef = useRef();
  const playTimer = useRef(null);

  // useMediaModal({ isInline, src });

  useEvent(
    'keydown',
    'window',
    e => {
      if (
        (isOpen || isPlaying) &&
        (e.key === 'Esc' || e.key === 'Escape')
      ) {
        clearTimeout(playTimer.current);
        setIsOpen(false);
        setIsPlaying(false);
      }
    },
    [isOpen, isPlaying, setIsPlaying, setIsOpen]
  );

  useEffect(() => {
    if (!isOpen) {
      clearTimeout(playTimer.current);
      setIsPlaying(false);
    }
  }, [isOpen, setIsPlaying]);

  const [Player, setPlayer] = useState(null);
  const canPlay = src && Player?.canPlay(src);

  useLayoutEffect(() => {
    import('react-player').then(Player => setPlayer(() => Player.default));
  }, []);

  const player = useMemo(() => {
    const _playerProps = playerProps ?? {};

    return !canPlay || !Player ? null : (
      <div
        className={classNames(styles.wrapper, playerWrapperClassName, {
          [styles.loadingDuration]: !isInline && isLoadingDuration
        })}
        style={{ ...style, maxWidth }}
      >
        <div className={styles.playerWrapper}>
          <Player
            url={src}
            className={classNames(styles.player, playerClassName)}
            playing={isOpen && isPlaying}
            style={playerStyle}
            width="100%"
            height="100%"
            config={{
              youtube: {
                playerVars: {
                  // origin: isBrowser() ? window.location.origin : null,
                  rel: 0
                }
              }
            }}
            controls
            onDuration={duration => {
              setDuration(duration);
              setIsLoadingDuration(false);
            }}
            onError={() => setIsLoadingDuration(false)}
            {..._playerProps}
          />
        </div>
      </div>
    );
  }, [
    Player,
    playerProps,
    canPlay,
    playerWrapperClassName,
    isInline,
    isLoadingDuration,
    style,
    maxWidth,
    src,
    playerClassName,
    isOpen,
    isPlaying,
    playerStyle
  ]);

  if (Player && !canPlay) {
    return null;
  }

  return isInline ? (
    player
  ) : (
    <Card
      title={title}
      className={classNames(styles.card, className, {
        [styles.hidden]: isOpen
      })}
      onClick={() => setIsOpen(true)}
      ref={previewRef}
      thumbnail={
        <Thumbnail
          image={thumbnail}
          videoUrl={src}
          duration={duration}
        />
      }
      thumbnailHeight={thumbnailHeight}
      isClickable
    >
      {children}
      <VideoModal
        isOpen={isOpen}
        previewRef={previewRef}
        setIsOpen={setIsOpen}
        setIsPlaying={setIsPlaying}
        player={player}
        playTimer={playTimer}
      />
    </Card>
  );
}

// TODO: Default to fetching thumbnail from
// YouTube if none is passed in

// TODO: Make media modal a singleton loaded from the
// main Layout component to prevent loading resources multiple times
const Thumbnail = React.memo(({ image, duration, videoUrl }) => {
  if (isNumber(duration)) {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration - minutes * 60);
    const timeArr = [minutes, seconds].map(t => {
      return t >= 10 ? t : `0${t}`;
    });

    if (hours > 0) {
      timeArr.unshift(hours);
    }

    duration = timeArr.join(':').replace(/^0/, '');
  }

  return (
    <div
      className={styles.thumbnail}
      style={{ backgroundImage: `url(${image})` }}
    >
      <div className={styles.iconWrapper}>
        <PlayButton className={styles.play} />
      </div>
      <span className={styles.duration}>{duration}</span>
    </div>
  );
});

const VideoModal = React.memo(
  ({ isOpen, previewRef, player, setIsOpen, setIsPlaying, playTimer }) => {
    const hasInitialized = useRef(false);
    const wrapper = useRef();
    const modal = useRef();
    const bg = useRef();
    const [bgTransform, setBgTransform] = useState(null);

    const isOpenRef = useRef(false);

    useEffect(() => {
      isOpenRef.current = isOpen;
    }, [isOpen]);

    const toggleModal = useCallback(() => {
      if (isOpen && modal.current && previewRef.current) {
        document.activeElement?.blur();
        const modalRect = modal.current.getBoundingClientRect();
        clearTimeout(playTimer.current);

        if (modalRect.height === 0) {
          hasInitialized.current = true;
          return;
        }

        const previewRect = previewRef.current.getBoundingClientRect();
        const x = previewRect.left - modalRect.left;
        const y = previewRect.top - modalRect.top;
        const scaleX = previewRect.width / modalRect.width;
        const scaleY = previewRect.height / modalRect.height;

        setBgTransform(
          `translate3d(${x}px, ${y}px, 0) scale(${scaleX}, ${scaleY})`
        );

        if (!hasInitialized.current) {
          setIsPlaying(isOpenRef.current);
        } else {
          playTimer.current = setTimeout(() => {
            if (isOpenRef.current) {
              setIsPlaying(true);
            }
          }, 200);
        }

        hasInitialized.current = true;
      } else {
        clearTimeout(playTimer.current);
        setIsPlaying(false);
        setBgTransform(null);
      }
    }, [isOpen, playTimer, previewRef, setIsPlaying]);

    useEffect(() => {
      const el = modal.current;
      let observer = null;

      if (!hasInitialized.current && el) {
        observer = new ResizeObserver(toggleModal);
        observer.observe(el);
      } else {
        toggleModal();
      }

      return () => {
        if (el && observer) {
          observer.unobserve(el);
        }
      };
    }, [toggleModal]);

    return createPortal(
      <RemoveScroll enabled={isOpen}>
        <FocusLock disabled={!isOpen}>
          <div
            ref={wrapper}
            className={classNames(styles.modalWrapper, {
              [styles.open]: isOpen && bgTransform
            })}
          >
            <div
              className={styles.modal}
              ref={modal}
            >
              <div className={styles.modalPlayer}>
                {((!hasInitialized.current && !isOpen) || isOpen) &&
                  player}
              </div>
              <div
                ref={bg}
                className={styles.bg}
                style={{ transform: bgTransform }}
              />
            </div>
            <div
              className={styles.wrapperBg}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsPlaying(false);
                setIsOpen(false);
              }}
            />
          </div>
        </FocusLock>
      </RemoveScroll>
    );
  }
);
